import { graphql } from '../graphql';

export const myProjectsQuery = graphql(`
  query MyProjects {
    projects {
      id
      name
      srid
      organizationId
      folderId
      storageUsedBytes
      archiveStatus
      archiveBytes
      retentionUntil
    }
  }
`);

export const archivedProjectsQuery = graphql(`
  query ArchivedProjects {
    archivedProjects {
      id
      name
      organizationId
      archiveStatus
      archivedAt
      archiveBytes
      archiveNonArchivedBytes
      retentionUntil
      restoreRequestedAt
      restoreRetrievedAt
      restoreExpiresAt
      archiveError
      storageUsedBytes
    }
  }
`);

export const archivePreviewQuery = graphql(`
  query ArchivePreview($projectId: ID!) {
    archivePreview(projectId: $projectId) {
      archivableBytes
      nonArchivableBytes
      archivableFileCount
      nonArchivableFileCount
      monthlyCostCurrent
      monthlyCostArchived
      retrievalFeeEstimate
      quotaAfterArchive
      retentionEndsAt
      blockingJobs {
        id
        type
        status
        label
      }
    }
  }
`);

export const getProjectQuery = graphql(`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      name
      description
      srid
      proj4String
      organizationId
      initialPosition
      initialAngle
      defaultThirdPersonView
      archiveStatus
      archiveBytes
      retentionUntil
      storageUsedBytes
      ephemeralBytes
      baseMaps {
        id
        name
        type
        url
        tileSize
        wmsVersion
        wmsLayers
        format
      }
    }
  }
`);

export const sharedLinkQuery = graphql(`
  query SharedLink {
    sharedLink {
      id
      allowQuickUploads
      expiresAt
      hasPassword
    }
  }
`);

export const getSharedProjectQuery = graphql(`
  query GetSharedProject {
    sharedProject {
      id
      name
      srid
      proj4String
      organizationId
      initialPosition
      initialAngle
      defaultThirdPersonView
      baseMaps {
        id
        name
        type
        url
        tileSize
        wmsVersion
        wmsLayers
        format
      }
      organization {
        name
        slug
        logo {
          url
        }
      }
    }
  }
`);

export const createProjectMutation = graphql(`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
      name
      srid
    }
  }
`);

/**
 * The PROJECT-WIDE job list — the one every 2 s progress poll reads.
 *
 * Deliberately WITHOUT `inputJson`, `outputJson` and `logs`. Those are
 * unbounded: a single GEOJSON import job on the project this was reported
 * from carries a 7 MB `outputJson`, and the whole list was being fetched
 * every 2 seconds for the duration of every run. At that size a response
 * outlives the poll interval, which used to mean it was never delivered at
 * all (the tick cancelled and restarted it for ever) — the toast and the
 * cross-section overlay badge span until the tab was reloaded.
 *
 * No consumer of this query reads those three fields on the polling path;
 * the one that needs `outputJson.renderError` (`useReportProgress`) asks
 * `jobsByBatch` for the one batch it is tracking. Anything that needs a
 * job's payload or logs should do the same — per batch or per job, never
 * per project on a timer.
 */
export const getProjectJobsQuery = graphql(`
  query GetProjectJobs($projectId: ID!) {
    jobs(projectId: $projectId) {
      id
      type
      status
      errorLogs
      startedAt
      completedAt
      estimatedCompletion
      leaseUntil
      attempts
      isLeaf
      batchId
      parentItemId
      createdAt
      updatedAt
    }
  }
`);

export const getProjectJobsByBatchQuery = graphql(`
  query GetProjectJobsByBatch($batchId: ID!) {
    jobs: jobsByBatch(batchId: $batchId) {
      id
      type
      status
      inputJson
      outputJson
      logs
      errorLogs
      startedAt
      completedAt
      estimatedCompletion
      leaseUntil
      attempts
      isLeaf
      batchId
      parentItemId
      createdAt
      updatedAt
    }
  }
`);

export const getProjectLinksQuery = graphql(`
  query GetProjectLinks($projectId: ID!) {
    projectLinks(projectId: $projectId) {
      id
      projectId
      name
      token
      hasPassword
      expiresAt
      createdAt
      createdById
      status
      viewCount
      allowQuickUploads
      ephemeralBytes
      createdBy {
        id
        name
        email
        avatar {
          url
        }
      }
    }
  }
`);

export const getAllProjectLinksQuery = graphql(`
  query GetAllProjectLinks {
    allProjectLinks {
      id
      projectId
      project {
        id
        name
      }
      name
      token
      hasPassword
      expiresAt
      createdAt
      createdById
      status
      viewCount
      allowQuickUploads
      ephemeralBytes
      createdBy {
        id
        name
        email
        avatar {
          url
        }
      }
    }
  }
`);
